import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NotificationsSettingsService } from 'src/app/core/data-services';
import { AuthenticationService, AuthorizationService } from 'src/app/core/services';
import { Notification } from 'src/app/shared/models';
import { BannerNotificationsService, PaginatedList } from 'src/app/xform-compat';

/**
 * Notifications menu component that displays recent notifications.
 * Used within Angular Material's mat-menu for proper positioning and behavior.
 */
@Component({
  selector: 'rms-notifications-menu',
  templateUrl: './notifications-menu.component.html',
  styleUrls: ['./notifications-menu.component.scss'],
  standalone: false
})
export class NotificationsMenuComponent implements OnInit {
  @Output() closeMenu = new EventEmitter<void>();

  public activeTab = 'RMS';
  public linkPath = '/notifications';
  public canReadCrm = false;
  public loading = false;
  public results: PaginatedList<Notification>;
  public notifications: Array<Notification>;
  public crmUnreaded: number = null;
  public rmsUnreaded: number = null;

  constructor(
    private authorizationService: AuthorizationService,
    private authenticationService: AuthenticationService,
    private notificationsSettingsService: NotificationsSettingsService,
    private bannerNotificationsService: BannerNotificationsService
  ) {}

  ngOnInit(): void {
    this.canReadCrm = this.authorizationService.hasPermission(['SONAR_READ']);
    this.activeTab = this.canReadCrm ? 'SONAR' : 'RMS';
    if (this.canReadCrm) {
      this.crmUnreaded = this.notificationsSettingsService.getCrmUnreadTotal();
      this.rmsUnreaded = this.notificationsSettingsService.getRmsUnreadTotal();
    }
    this.getNotifications();
  }

  /**
   * Get notifications from BE
   */
  public getNotifications(): void {
    const userId = this.authenticationService.getLoggedUser()?.id;
    if (!userId) {
      return;
    }

    this.loading = true;

    const params = {
      direction: 'DESC',
      page: 0,
      pagesize: 5,
      sort: 'created',
      notificationtype: ['COMPANY', 'USER'],
      source: this.activeTab,
      historydays: 6 // Get only the last 7 business days.
    };

    this.notificationsSettingsService.getNotificationsByUser(userId, params)
      .subscribe(
        result => {
          this.loading = false;
          this.results = result;
          this.notifications = result.records;
        },
        (error) => {
          this.loading = false;
          this.bannerNotificationsService.error(error);
        }
      );
  }

  /**
   * Switch the tab selection
   * @param tab Tab to switch
   * @param event Mouse event to stop propagation
   */
  public changeTab(event: Event, tab: string): void {
    event.stopPropagation();
    if (tab !== this.activeTab) {
      this.activeTab = tab;
      this.getNotifications();
    }
  }

  /**
   * Handles notification link click - closes the menu
   * @param event Mouse event to stop propagation
   */
  public onNotificationClick(event: MouseEvent): void {
    event.stopPropagation();
    this.closeMenu.emit();
  }

  /**
   * Handles "See All" link click - closes the menu
   * @param event Mouse event to stop propagation
   */
  public onSeeAllClick(event: MouseEvent): void {
    event.stopPropagation();
    this.closeMenu.emit();
  }
}
